import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Plus,
  Upload,
  Eye,
  CheckCircle2,
  XCircle,
  PackageOpen,
  FileDown,
  ClipboardList,
  PenSquare
} from 'lucide-react';
import DataTable from './DataTable';
import {
  formatCurrency,
  formatDate,
  getStatusText,
  filesToAttachments,
  formatFileSize
} from '../utils/helpers';
import { generatePurchaseOrderPdf } from '../utils/pdf';
import { useAppContext } from '../context/AppContext';
import EmptyState from './common/EmptyState';
import Badge from './common/Badge';
import Button from './common/Button';
import { t } from '../i18n';

// Tono semántico de Badge para cada estado de documento (mismo criterio que
// getStatusColor/getStatusText en utils/helpers.js).
const STATUS_TONE = {
  pendiente: 'warning',
  pendiente_pm: 'warning',
  pendiente_area: 'warning',
  pendiente_compras: 'warning',
  pendiente_controller: 'warning',
  pendiente_compra: 'warning',
  en_licitacion: 'info',
  borrador: 'neutral',
  convertida: 'success',
  aprobada: 'success',
  enviada: 'info',
  recibida: 'success',
  rechazada: 'danger'
};
const getStatusTone = (status) => STATUS_TONE[status] || 'neutral';

const initialItem = () => ({
  sku: '',
  description: '',
  quantity: 1,
  unit: 'unidad',
  unitPrice: 0
});

const createLifecycleNode = () => ({
  status: 'pending',
  timestamp: null,
  userId: null,
  note: '',
  referenceId: null
});

const normalizeLifecycle = (lifecycle = {}) => ({
  received: { ...createLifecycleNode(), ...lifecycle.received },
  invoiced: { ...createLifecycleNode(), ...lifecycle.invoiced },
  paid: { ...createLifecycleNode(), ...lifecycle.paid }
});

const formatTimestamp = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return `${date.toLocaleDateString('es-CL')} ${date.toLocaleTimeString('es-CL', {
    hour: '2-digit',
    minute: '2-digit'
  })}`;
};

const PurchaseOrders = () => {
  const {
    currentUser,
    purchaseOrders,
    purchaseRequisitions,
    suppliers,
    locations,
    parks,
    categories,
    createPurchaseOrder,
    convertRequisitionToOrder,
    updatePurchaseOrder,
    setPurchaseOrderBudgetIncrementAcceptance,
    advancePurchaseOrderApproval,
    markPurchaseOrderReceived,
    markPurchaseOrderInvoiced,
    markPurchaseOrderPaid,
    markPurchaseOrderSent,
    findUserById,
    findCostCenterById,
    findAreaById,
    findParkById,
    findPackageById,
    getProjectBudgetSummary
  } = useAppContext();

  if (!parks || parks.length === 0) {
    return (
      <EmptyState
        title={t('Órdenes de compra: no hay proyectos para imputar')}
        description={t('Para emitir una OC necesitas un proyecto (y normalmente una PR previa).')}
        checklist={[
          'Crear un proyecto en Administración o Desarrollo.',
          'Configurar Área + CECO y WBS/Paquetes.',
          'Definir categorías y presupuesto.'
        ]}
        primaryAction={{ label: 'Ir a Administración', pageId: 'admin' }}
        secondaryAction={{ label: 'Ir a Desarrollo', pageId: 'development-portfolio' }}
      />
    );
  }

  if (!categories || categories.length === 0) {
    return (
      <EmptyState
        title={t('Órdenes de compra: faltan categorías')}
        description={t('Las categorías son obligatorias para control presupuestario y reporting.')}
        checklist={['Crear categorías (con categoría presupuestaria) en Administración.']}
        primaryAction={{ label: 'Ir a Administración', pageId: 'admin' }}
      />
    );
  }

  if (!suppliers || suppliers.length === 0) {
    return (
      <EmptyState
        title={t('Órdenes de compra: faltan proveedores')}
        description={t(
          'Para emitir una OC debes tener al menos un proveedor (idealmente homologado).'
        )}
        checklist={['Crear proveedor en Proveedores & Compliance (o cargar templates) antes de emitir OC.']}
        primaryAction={{ label: 'Ir a Proveedores', pageId: 'suppliers-overview' }}
        secondaryAction={{ label: 'Ir a Administración', pageId: 'admin' }}
      />
    );
  }

  const defaultPark = parks?.[0];
  const defaultArea = defaultPark?.areas?.[0];
  const defaultCategory = categories?.[0];

  const [filter, setFilter] = useState('todos');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [editNote, setEditNote] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [observation, setObservation] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [formError, setFormError] = useState('');
  const [manualPrInput, setManualPrInput] = useState('');
  const [formState, setFormState] = useState({
    supplierId: suppliers[0]?.id || '',
    type: 'internacional',
    linkedRequisitionId: '',
    parkId: defaultPark?.id || '',
    areaId: defaultArea?.id || '',
    costCenterId: defaultArea?.costCenterId || '',
    categoryId: defaultCategory?.id || '',
    deliveryLocationId: locations[0]?.id || '',
    deliveryDate: '',
    notes: '',
    attachments: [],
    items: [initialItem()]
  });
  const [financeNotes, setFinanceNotes] = useState({
    invoiceRef: '',
    invoiceNote: '',
    paymentRef: '',
    paymentNote: ''
  });
  const isEditing = modalMode === 'edit';
  const editableStatuses = ['aprobada', 'pendiente_compras'];
  const canCurrentUserEdit = ['comprador', 'project_manager', 'purchasing_manager', 'super_user'].includes(
    currentUser.role
  );

  const getOrderWorkflowInfo = (order) => {
    if (!order) {
      return {
        stageKey: 'unknown',
        stageLabel: 'Sin estado',
        ownerName: '-',
        ownerId: null
      };
    }

    const approvals = order.approvals || {};
    const stageMeta = {
      pendiente_pm: {
        label: t('Aprobación Project Manager'),
        node: approvals.projectManager,
        fallback: 'Project Manager'
      },
      pendiente_compras: {
        label: t('Aprobación Gerencia de Compras'),
        node: approvals.purchasingManager,
        fallback: 'Gerencia de Compras'
      }
    };

    if (stageMeta[order.status]) {
      const meta = stageMeta[order.status];
      const ownerId = meta.node?.userId || null;
      const ownerName = ownerId ? findUserById(ownerId)?.name || meta.fallback : meta.fallback;
      return {
        stageKey: order.status,
        stageLabel: meta.label,
        ownerName,
        ownerId
      };
    }

    switch (order.status) {
      case 'aprobada':
        return {
          stageKey: 'aprobada',
          stageLabel: 'PO aprobada',
          ownerName: 'Compras',
          ownerId: null
        };
      case 'enviada':
        return {
          stageKey: 'enviada',
          stageLabel: 'PO enviada a proveedor',
          ownerName: 'Compras',
          ownerId: null
        };
      case 'recibida':
        return {
          stageKey: 'recibida',
          stageLabel: 'Recepción completada',
          ownerName: 'Almacén',
          ownerId: null
        };
      case 'rechazada':
        return {
          stageKey: 'rechazada',
          stageLabel: 'PO rechazada',
          ownerName: 'Revisar comentarios',
          ownerId: null
        };
      default:
        return {
          stageKey: order.status || 'unknown',
          stageLabel: getStatusText(order.status) || 'Sin estado',
          ownerName: '-',
          ownerId: null
        };
    }
  };

  const visibleOrders = useMemo(() => purchaseOrders, [purchaseOrders]);

  const statusCounters = useMemo(() => {
    return visibleOrders.reduce(
      (acc, order) => {
        acc.total += 1;
        acc[order.status] = (acc[order.status] || 0) + 1;
        return acc;
      },
      { total: 0 }
    );
  }, [visibleOrders]);

  const orderWorkflowCounters = useMemo(() => {
    const totals = {};
    const mine = {};
    visibleOrders.forEach((order) => {
      const workflow = getOrderWorkflowInfo(order);
      if (workflow.stageKey) {
        totals[workflow.stageKey] = (totals[workflow.stageKey] || 0) + 1;
        if (workflow.ownerId && workflow.ownerId === currentUser?.id) {
          mine[workflow.stageKey] = (mine[workflow.stageKey] || 0) + 1;
        }
      }
    });
    return { totals, mine };
  }, [visibleOrders, currentUser?.id, findUserById]);

  const filteredOrders = useMemo(() => {
    if (filter === 'todos') return visibleOrders;
    return visibleOrders.filter((order) => {
      if (filter === 'pendiente_compras') {
        return order.status === 'pendiente_compras';
      }
      return order.status === filter;
    });
  }, [visibleOrders, filter]);

  const orderWorkflowCards = useMemo(() => {
    const cardMeta = [
      {
        key: 'pendiente_pm',
        label: t('Aprobación Project Manager'),
        tagline: 'Autorización inicial del proyecto'
      },
      {
        key: 'pendiente_compras',
        label: t('Aprobación Gerencia de Compras'),
        tagline: 'Cierre del proceso de autorización'
      }
    ];

    return cardMeta
      .map((meta) => ({
        ...meta,
        count: orderWorkflowCounters.totals[meta.key] || 0,
        mine: orderWorkflowCounters.mine[meta.key] || 0
      }))
      .filter((card) => card.count > 0 || card.mine > 0);
  }, [orderWorkflowCounters]);

  const tableOrders = useMemo(() => {
    return filteredOrders.map((order) => {
      const area = order.areaId ? findAreaById(order.areaId) : null;
      const park = order.parkId ? findParkById(order.parkId) : area ? { id: area.parkId, name: area.parkName } : null;
      const costCenter = order.costCenterId ? findCostCenterById(order.costCenterId) : null;
      const category = order.categoryId ? categories.find((cat) => cat.id === order.categoryId) : null;
      const workflow = getOrderWorkflowInfo(order);
      return {
        ...order,
        parkName: park?.name || '-',
        areaName: area?.name || '-',
        costCenterCode: costCenter?.code || '-',
        costCenterName: costCenter?.name || costCenter?.code || '-',
        categoryName: category?.name || order.categoryName || '-',
        workflowStage: workflow.stageLabel,
        workflowOwner: workflow.ownerName,
        workflowKey: workflow.stageKey,
        workflowOwnerId: workflow.ownerId,
        rawOrder: order
      };
    });
  }, [filteredOrders, findAreaById, findParkById, findCostCenterById, categories, findUserById]);

  const parkAreas = useMemo(() => {
    const park = formState.parkId ? findParkById(formState.parkId) : defaultPark;
    return park?.areas || [];
  }, [formState.parkId, findParkById, defaultPark]);

  const currentCostCenter = formState.costCenterId ? findCostCenterById(formState.costCenterId) : null;

  const getApprovalStage = (order) => {
    switch (order.status) {
      case 'pendiente_pm':
        return 'project';
      case 'pendiente_compras':
        return 'purchasing';
      default:
        return null;
    }
  };

  const handleApproval = (order, decision) => {
    const stage = getApprovalStage(order);
    if (!stage) return;
    advancePurchaseOrderApproval(order.id, stage, decision, observation);
    setObservation('');
  };

  const handleReception = (order) => {
    markPurchaseOrderReceived(order.id, observation);
    setObservation('');
  };

  const selectedArea = selectedOrder ? findAreaById(selectedOrder.areaId) : null;
  const selectedPark = selectedOrder
    ? selectedOrder.parkId
      ? findParkById(selectedOrder.parkId)
      : selectedArea
        ? findParkById(selectedArea.parkId)
        : null
    : null;
  const selectedCostCenter = selectedOrder?.costCenterId ? findCostCenterById(selectedOrder.costCenterId) : null;
  const selectedCategory = selectedOrder?.categoryId ? categories.find((cat) => cat.id === selectedOrder.categoryId) : null;
  const selectedPackage = selectedOrder?.packageId && selectedPark
    ? findPackageById(selectedPark.id, selectedOrder.packageId)
    : null;
  const selectedStage = selectedOrder ? getApprovalStage(selectedOrder) : null;
  const lifecycle = useMemo(() => normalizeLifecycle(selectedOrder?.lifecycle), [selectedOrder]);
  const canEditSelectedOrder =
    !!selectedOrder && canCurrentUserEdit && editableStatuses.includes(selectedOrder.status);
  const receivingRoles = useMemo(() => ['comprador', 'purchasing_manager', 'super_user'], []);
  const financeRoles = useMemo(() => ['project_manager', 'purchasing_manager', 'super_user'], []);
  const canMarkReceivedLifecycle =
    !!selectedOrder &&
    (receivingRoles.includes(currentUser.role) || currentUser.id === selectedOrder.createdBy) &&
    lifecycle.received.status !== 'completed';
  const canMarkInvoicedLifecycle =
    !!selectedOrder &&
    financeRoles.includes(currentUser.role) &&
    lifecycle.invoiced.status !== 'completed';
  const canMarkPaidLifecycle =
    !!selectedOrder &&
    financeRoles.includes(currentUser.role) &&
    lifecycle.paid.status !== 'completed';
  const handleExportOrder = () => {
    if (!selectedOrder) return;
    const location = locations.find((loc) => loc.id === selectedOrder.deliveryLocationId);
    generatePurchaseOrderPdf(selectedOrder, {
      supplierName: selectedOrder.supplierName,
      locationName: location?.name,
      parkName: selectedPark?.name,
      costCenter: selectedCostCenter ? `${selectedCostCenter.code} · ${selectedCostCenter.name}` : '-',
      generatedBy: currentUser?.name || 'Sistema'
    });
  };
  const isCurrentApprover = selectedOrder
    ? (selectedStage === 'project' && selectedOrder.approvals?.projectManager?.userId === currentUser.id) ||
      (selectedStage === 'area' && selectedOrder.approvals?.areaManager?.userId === currentUser.id) ||
      (selectedStage === 'purchasing' && selectedOrder.approvals?.purchasingManager?.userId === currentUser.id)
    : false;
  const budgetImpact = selectedOrder ? getProjectBudgetSummary(selectedOrder.parkId) : null;
  const orderBudgetCategory = (() => {
    if (!selectedOrder) return 'otros';
    if (selectedOrder.categoryId) {
      const category = categories.find((cat) => cat.id === selectedOrder.categoryId);
      if (category) return category.budgetCategory || 'otros';
    }
    if (selectedOrder.categoryName) {
      const category = categories.find((cat) => cat.name === selectedOrder.categoryName);
      if (category) return category.budgetCategory || 'otros';
    }
    return 'otros';
  })();
  const budgetAllocation = selectedOrder && budgetImpact
    ? budgetImpact.allocations.find((allocation) => allocation.category === orderBudgetCategory)
    : null;
  const budgetControl = selectedOrder?.budgetControl || null;
  const budgetOverrunActive = Boolean(budgetControl?.hasOverrun);
  const pmBudgetAccepted = Boolean(budgetControl?.projectManagerAcceptedIncrement);
  const budgetOverrunAmount = Number(budgetControl?.overrunDetail?.overrunAmount || 0);
  const budgetAvailableAmount = Number(budgetControl?.overrunDetail?.allocationAvailable || 0);
  const canConfirmBudgetIncrement =
    !!selectedOrder &&
    budgetOverrunActive &&
    (currentUser.role === 'super_user' ||
      currentUser.id === selectedOrder.approvals?.projectManager?.userId);

  useEffect(() => {
    setFinanceNotes({
      invoiceRef: '',
      invoiceNote: '',
      paymentRef: '',
      paymentNote: ''
    });
  }, [selectedOrder]);

  useEffect(() => {
    if (!selectedOrder) return;
    const refreshed = purchaseOrders.find((order) => order.id === selectedOrder.id);
    if (!refreshed) {
      setSelectedOrder(null);
    } else if (refreshed !== selectedOrder) {
      setSelectedOrder(refreshed);
    }
  }, [purchaseOrders, selectedOrder]);

  const orderColumns = [
    { key: 'id', label: t('N° Orden'), sortable: true },
    {
      key: 'supplierName',
      label: t('Proveedor'),
      sortable: true,
      render: (supplierName, row) => (
        <div>
          <p className="font-semibold text-slate-800">{t(supplierName)}</p>
          <p className="text-xs text-slate-500">{row.type === 'internacional' ? t('Materiales') : t('Servicios')}</p>
        </div>
      )
    },
    {
      key: 'type',
      label: t('Tipo'),
      sortable: true,
      render: (type) => (
        <Badge tone="neutral">{type === 'internacional' ? t('Internacional') : t('Nacional')}</Badge>
      )
    },
    {
      key: 'totalValue',
      label: t('Valor Total'),
      sortable: true,
      align: 'right',
      render: (value) => <span className="font-semibold text-slate-900">{formatCurrency(value)}</span>
    },
    {
      key: 'parkName',
      label: t('Parque'),
      sortable: true,
      render: (value) => value || '-'
    },
    {
      key: 'areaName',
      label: t('Área'),
      sortable: true,
      render: (value) => value || '-'
    },
    {
      key: 'costCenterCode',
      label: t('Centro Costo'),
      sortable: true,
      render: (_, row) => (
        <div>
          <p className="font-semibold text-slate-800">{t(row.costCenterCode) || '-'}</p>
          <p className="text-xs text-slate-400">{t(row.costCenterName) || ''}</p>
        </div>
      )
    },
    {
      key: 'categoryName',
      label: t('Categoría'),
      sortable: true,
      render: (value) => value || '-'
    },
    {
      key: 'workflowStage',
      label: t('Siguiente paso'),
      render: (_, row) => (
        <div>
          <p className="text-sm font-semibold text-slate-800">{t(row.workflowStage) || '-'}</p>
          <p className="text-xs text-slate-500">{t(row.workflowOwner) || '—'}</p>
        </div>
      )
    },
    {
      key: 'status',
      label: t('Estado'),
      sortable: true,
      render: (status) => <Badge tone={getStatusTone(status)}>{t(getStatusText(status))}</Badge>
    },
    {
      key: 'lifecycleSummary',
      label: t('Seguimiento'),
      render: (_, row) => {
        const lifecycle = normalizeLifecycle((row.rawOrder || row).lifecycle);
        const entries = [
          { label: t('Recepcionado'), node: lifecycle.received },
          { label: t('Facturado'), node: lifecycle.invoiced },
          { label: t('Pagado'), node: lifecycle.paid }
        ];
        return (
          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
            {entries.map((entry) => (
              <Badge key={entry.label} tone={entry.node.status === 'completed' ? 'success' : 'neutral'}>
                {t(entry.label)}
              </Badge>
            ))}
          </div>
        );
      }
    },
    {
      key: 'createdBy',
      label: t('Creado por'),
      sortable: true,
      render: (userId) => findUserById(userId)?.name || '-'
    },
    {
      key: 'createdDate',
      label: t('Fecha creación'),
      sortable: true,
      render: (date) => formatDate(date)
    },
    {
      key: 'actions',
      label: t('Acciones'),
      render: (_, order) => (
        <div className="flex items-center gap-2">
          <button
            className="p-2 rounded-lg text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition"
            onClick={() => setSelectedOrder(order.rawOrder || order)}
          >
            <Eye className="w-4 h-4" />
          </button>
          {getApprovalStage(order.rawOrder || order) === 'project' &&
            currentUser.id === (order.rawOrder || order).approvals?.projectManager?.userId && (
              <>
                <button
                  className="p-2 rounded-lg text-success-600 hover:text-success-700 hover:bg-success-50 transition"
                  onClick={() => handleApproval(order.rawOrder || order, true)}
                >
                  <CheckCircle2 className="w-4 h-4" />
                </button>
                <button
                  className="p-2 rounded-lg text-danger-600 hover:text-danger-700 hover:bg-danger-50 transition"
                  onClick={() => handleApproval(order.rawOrder || order, false)}
                >
                  <XCircle className="w-4 h-4" />
                </button>
              </>
            )}
          {getApprovalStage(order.rawOrder || order) === 'purchasing' &&
            currentUser.id === (order.rawOrder || order).approvals?.purchasingManager?.userId && (
              <>
                <button
                  className="p-2 rounded-lg text-success-600 hover:text-success-700 hover:bg-success-50 transition"
                  onClick={() => handleApproval(order.rawOrder || order, true)}
                >
                  <CheckCircle2 className="w-4 h-4" />
                </button>
                <button
                  className="p-2 rounded-lg text-danger-600 hover:text-danger-700 hover:bg-danger-50 transition"
                  onClick={() => handleApproval(order.rawOrder || order, false)}
                >
                  <XCircle className="w-4 h-4" />
                </button>
              </>
            )}
          {(order.rawOrder || order).status === 'aprobada' &&
            ['purchasing_manager', 'comprador', 'super_user'].includes(currentUser.role) && (
              <button
                className="p-2 rounded-lg text-brand-600 hover:text-brand-700 hover:bg-brand-50 transition"
                onClick={() => handleReception(order.rawOrder || order)}
              >
                <PackageOpen className="w-4 h-4" />
              </button>
            )}
        </div>
      )
    }
  ];

  const filterButtons = [
    { key: 'todos', label: t('Todas'), count: statusCounters.total },
    { key: 'pendiente_pm', label: t('Pendiente PM'), count: statusCounters.pendiente_pm || 0 },
    {
      key: 'pendiente_compras',
      label: t('Pendiente Compras'),
      count: statusCounters.pendiente_compras || 0
    },
    { key: 'aprobada', label: t('Aprobadas'), count: statusCounters.aprobada || 0 },
    { key: 'enviada', label: t('Enviadas'), count: statusCounters.enviada || 0 },
    { key: 'recibida', label: t('Recibidas'), count: statusCounters.recibida || 0 },
    { key: 'rechazada', label: t('Rechazadas'), count: statusCounters.rechazada || 0 }
  ];

  const orderTotal = formState.items.reduce(
    (sum, item) => sum + Number(item.quantity || 0) * Number(item.unitPrice || 0),
    0
  );

  const pendingRequisitions = useMemo(() => {
    const allowedStatuses = new Set(['en_licitacion']);
    return (purchaseRequisitions || [])
      .filter((req) => allowedStatuses.has(req.status) && !req.linkedOrderId)
      .sort((a, b) => (a.createdDate < b.createdDate ? 1 : -1));
  }, [purchaseRequisitions]);

  const getRequisitionLabel = useCallback(
    (req) => {
      const park = req.parkId ? findParkById(req.parkId) : null;
      const parkName = park?.name || 'Proyecto sin asignar';
      return `${req.id} · ${parkName}`;
    },
    [findParkById]
  );

  const applyRequisitionToForm = useCallback(
    (req) => {
      if (!req) return;
      const park = req.parkId ? findParkById(req.parkId) : null;
      const area = req.areaId ? findAreaById(req.areaId) : null;
      const requisitionItems = (req.lines || []).map((line) => ({
        sku: line.sku || '',
        description: line.description || '',
        quantity: Number(line.quantity || 0),
        unit: line.unit || 'unidad',
        unitPrice: Number(line.unitPrice || 0)
      }));
      setFormState((prev) => ({
        ...prev,
        linkedRequisitionId: req.id,
        type: req.type === 'servicio' ? 'nacional' : 'internacional',
        parkId: req.parkId || park?.id || prev.parkId,
        areaId: req.areaId || area?.id || prev.areaId,
        costCenterId: req.costCenterId || area?.costCenterId || prev.costCenterId,
        deliveryLocationId: req.deliveryLocationId || prev.deliveryLocationId,
        deliveryDate: req.neededDate || prev.deliveryDate,
        notes: prev.notes || `${req.title || 'Solicitud'} · ${req.id}`,
        items: requisitionItems.length > 0 ? requisitionItems : prev.items
      }));
      setManualPrInput(req.id);
      setFormError('');
    },
    [findAreaById, findParkById]
  );

  const resetForm = () => {
    const park = parks?.[0];
    const area = park?.areas?.[0];
    const category = categories?.[0];
    setFormState({
      supplierId: suppliers[0]?.id || '',
      type: 'internacional',
      linkedRequisitionId: '',
      parkId: park?.id || '',
      areaId: area?.id || '',
      costCenterId: area?.costCenterId || '',
      categoryId: category?.id || '',
      deliveryLocationId: locations[0]?.id || '',
      deliveryDate: '',
      notes: '',
      attachments: [],
      items: [initialItem()]
    });
    setUploadError('');
    setFormError('');
    setManualPrInput('');
  };

  const handleAttachmentUpload = async (event) => {
    const { files } = event.target;
    if (!files || files.length === 0) return;
    setUploadError('');
    try {
      const uploads = await filesToAttachments(files);
      if (uploads.length === 0) return;
      setFormState((prev) => ({
        ...prev,
        attachments: [...prev.attachments, ...uploads]
      }));
    } catch (error) {
      console.error(error);
      setUploadError(t('No fue posible adjuntar algunos archivos. Inténtalo nuevamente.'));
    } finally {
      event.target.value = '';
    }
  };

  const removeAttachment = (attachmentId) => {
    setFormState((prev) => ({
      ...prev,
      attachments: prev.attachments.filter((attachment) => attachment.id !== attachmentId)
    }));
  };

  const handleMarkInvoiced = () => {
    if (!selectedOrder) return;
    markPurchaseOrderInvoiced(selectedOrder.id, {
      invoiceId: financeNotes.invoiceRef || selectedOrder.lifecycle?.invoiced?.referenceId || null,
      note: financeNotes.invoiceNote
    });
    setFinanceNotes((prev) => ({
      ...prev,
      invoiceRef: '',
      invoiceNote: ''
    }));
  };

  const handleMarkPaid = () => {
    if (!selectedOrder) return;
    markPurchaseOrderPaid(selectedOrder.id, {
      paymentReference: financeNotes.paymentRef || selectedOrder.lifecycle?.paid?.referenceId || null,
      note: financeNotes.paymentNote
    });
    setFinanceNotes((prev) => ({
      ...prev,
      paymentRef: '',
      paymentNote: ''
    }));
  };

  const handleSendToSupplier = () => {
    if (!selectedOrder) return;
    markPurchaseOrderSent(selectedOrder.id, observation);
    setObservation('');
  };

  const lifecycleEntries = useMemo(
    () => [
      {
        key: 'received',
        title: t('Recepción'),
        description: t(
          'Comprador / solicitante / Gerencia de Compras confirman la recepción física.'
        ),
        node: lifecycle.received,
        canAction: false,
        actionLabel: '',
        onAction: null,
        renderForm: null
      },
      {
        key: 'invoiced',
        title: t('Facturación'),
        description: t('Finanzas marca cuando la factura queda posteada en FI.'),
        node: lifecycle.invoiced,
        canAction: canMarkInvoicedLifecycle,
        actionLabel: t('Marcar como facturada'),
        onAction: handleMarkInvoiced,
        renderForm: () => (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-500">{t('Referencia factura')}</label>
              <input
                value={financeNotes.invoiceRef}
                onChange={(event) =>
                  setFinanceNotes((prev) => ({ ...prev, invoiceRef: event.target.value }))
                }
                className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-brand-500/20"
                placeholder={t('Número o ID en FI')}
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-semibold text-slate-500">{t('Nota')}</label>
              <textarea
                value={financeNotes.invoiceNote}
                onChange={(event) =>
                  setFinanceNotes((prev) => ({ ...prev, invoiceNote: event.target.value }))
                }
                rows={2}
                className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-brand-500/20"
                placeholder={t('Detalle del posteo contable')}
              />
            </div>
          </div>
        )
      },
      {
        key: 'paid',
        title: t('Pago'),
        description: t('Tesorería confirma que la factura fue pagada y conciliada.'),
        node: lifecycle.paid,
        canAction: canMarkPaidLifecycle,
        actionLabel: t('Marcar como pagada'),
        onAction: handleMarkPaid,
        renderForm: () => (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-500">{t('Referencia de pago')}</label>
              <input
                value={financeNotes.paymentRef}
                onChange={(event) =>
                  setFinanceNotes((prev) => ({ ...prev, paymentRef: event.target.value }))
                }
                className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20"
                placeholder={t('Transferencia, doc. banco, etc.')}
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-semibold text-slate-500">{t('Nota')}</label>
              <textarea
                value={financeNotes.paymentNote}
                onChange={(event) =>
                  setFinanceNotes((prev) => ({ ...prev, paymentNote: event.target.value }))
                }
                rows={2}
                className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20"
                placeholder={t('Comentario de tesorería o referencia bancaria')}
              />
            </div>
          </div>
        )
      }
    ],
    [
      lifecycle,
      canMarkInvoicedLifecycle,
      canMarkPaidLifecycle,
      financeNotes,
      handleMarkInvoiced,
      handleMarkPaid,
      setFinanceNotes
    ]
  );

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!isEditing && !formState.linkedRequisitionId) {
      setFormError(t('Debes seleccionar una solicitud de pedido (PR) para generar la OC.'));
      return;
    }
    const supplier = suppliers.find((item) => item.id === formState.supplierId);
    const category = categories.find((cat) => cat.id === formState.categoryId);
    const payloadItems = formState.items.map((item) => ({
      ...item,
      quantity: Number(item.quantity),
      unitPrice: Number(item.unitPrice)
    }));

    const supplierName = supplier?.name || selectedOrder?.supplierName || '';
    const categoryName = category?.name || formState.categoryName || selectedOrder?.categoryName || '';
    const basePayload = {
      supplierId: formState.supplierId,
      supplierName,
      type: formState.type,
      linkedRequisitionId: formState.linkedRequisitionId || null,
      parkId: formState.parkId,
      areaId: formState.areaId,
      costCenterId: formState.costCenterId,
      categoryId: formState.categoryId,
      categoryName,
      deliveryLocationId: formState.deliveryLocationId,
      deliveryDate: formState.deliveryDate,
      notes: formState.notes,
      attachments: formState.attachments,
      items: payloadItems,
      totalValue: orderTotal
    };

    try {
      if (isEditing && editingOrderId) {
        const updated = updatePurchaseOrder(editingOrderId, basePayload, editNote);
        setSelectedOrder(updated);
      } else {
        if (formState.linkedRequisitionId) {
          convertRequisitionToOrder(formState.linkedRequisitionId, basePayload);
        } else {
          createPurchaseOrder(basePayload);
        }
      }
    } catch (error) {
      console.error('No fue posible registrar los cambios de la orden.', error);
      setFormError(error?.message || t('No fue posible registrar la orden por reglas de negocio.'));
      return;
    }

    setShowCreateModal(false);
    resetForm();
    setModalMode('create');
    setEditingOrderId(null);
    setEditNote('');
  };

  const handleItemChange = (index, key, value) => {
    setFormState((prev) => {
      const items = prev.items.map((item, itemIndex) =>
        itemIndex === index
          ? {
              ...item,
              [key]: key === 'quantity' || key === 'unitPrice' ? Number(value) : value
            }
          : item
      );
      return { ...prev, items };
    });
  };

  const addItemRow = () => {
    setFormState((prev) => ({ ...prev, items: [...prev.items, initialItem()] }));
  };

  const removeItemRow = (indexToRemove) => {
    setFormState((prev) => ({
      ...prev,
      items: prev.items.filter((_, index) => index !== indexToRemove)
    }));
  };

  const openCreateModal = () => {
    resetForm();
    setModalMode('create');
    setEditingOrderId(null);
    setEditNote('');
    setShowCreateModal(true);
  };

  const openEditModal = (order) => {
    if (!order) return;
    setModalMode('edit');
    setEditingOrderId(order.id);
    setEditNote('');
    setUploadError('');
    setFormState({
      supplierId: order.supplierId || '',
      type: order.type || 'internacional',
      linkedRequisitionId: order.linkedRequisitionId || '',
      parkId: order.parkId || '',
      areaId: order.areaId || '',
      costCenterId: order.costCenterId || '',
      categoryId: order.categoryId || '',
      deliveryLocationId: order.deliveryLocationId || '',
      deliveryDate: order.deliveryDate || '',
      notes: order.notes || '',
      attachments: (order.attachments || []).map((attachment) => ({ ...attachment })),
      items:
        (order.items && order.items.length > 0 ? order.items : [initialItem()]).map((item) => ({
          sku: item.sku || '',
          description: item.description || '',
          quantity: item.quantity != null ? Number(item.quantity) : 1,
          unit: item.unit || 'unidad',
          unitPrice: item.unitPrice != null ? Number(item.unitPrice) : 0
        }))
    });
    setShowCreateModal(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">{t('Órdenes de Compra')}</h1>
          <p className="text-sm text-slate-500">{t(
            'Flujos de compra internacionales (materiales) y nacionales (servicios), con aprobaciones multinivel.'
          )}</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 cursor-pointer hover:bg-slate-50 transition">
            <Upload className="w-4 h-4" />{t('Importar CSV')}<input type="file" className="hidden" accept=".csv" />
          </label>
          {['comprador', 'purchasing_manager', 'super_user'].includes(currentUser.role) && (
            <Button variant="primary" icon={Plus} onClick={openCreateModal}>{t('Nueva Orden')}</Button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {filterButtons.map((button) => (
          <button
            key={button.key}
            onClick={() => setFilter(button.key)}
            className={`px-4 py-2 rounded-xl text-sm font-semibold transition border ${
              filter === button.key
                ? 'bg-slate-900 text-white border-slate-900'
                : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
            }`}
          >
            {t(button.label)}
            <span
              className={`ml-2 inline-flex items-center justify-center px-2 py-0.5 rounded-lg text-xs font-semibold ${
                filter === button.key ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'
              }`}
            >
              {t(button.count)}
            </span>
          </button>
        ))}
      </div>

      {orderWorkflowCards.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {orderWorkflowCards.map((card) => (
            <div
              key={card.key}
              className="border border-slate-200 bg-white rounded-2xl px-4 py-3 shadow-sm"
            >
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                {t(card.label)}
              </p>
              <div className="flex items-center gap-3 mt-2">
                <span className="text-2xl font-bold text-slate-900">{t(card.count)}</span>
                {card.mine > 0 && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-slate-900 text-white">
                    {t(card.mine)} {t('a mi cargo')}</span>
                )}
              </div>
              <p className="mt-1 text-xs text-slate-500">{t(card.tagline)}</p>
            </div>
          ))}
        </div>
      )}

      <DataTable
        data={tableOrders}
        columns={orderColumns}
        searchPlaceholder={t('Buscar por número, proveedor o centro de costo...')}
        onRowClick={(order) => setSelectedOrder(order.rawOrder || order)}
        variant="classic"
      />

      {showCreateModal && (
          <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center z-50 p-6">
            <div className="bg-white rounded-3xl shadow-2xl border border-slate-100 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <div className="px-8 py-6 border-b border-slate-100 flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-slate-900">
                    {isEditing ? t('Editar Orden de Compra') : t('Nueva Orden de Compra')}
                  </h3>
                  <p className="text-sm text-slate-500">
                    {isEditing
                      ? t(
                      'Ajusta los datos económicos; los cambios quedarán pendientes de aprobación de Gerencia de Compras.'
                    )
                      : t('Completa la información de proveedor, entrega y detalle de ítems.')}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                    setModalMode('create');
                    setEditingOrderId(null);
                    setEditNote('');
                  }}
                  className="text-sm text-slate-500 hover:text-slate-900 transition"
                >{t('Cerrar')}</button>
              </div>

              <form className="p-8 space-y-8" onSubmit={handleSubmit}>
                {formError && (
                  <div className="rounded-xl border border-danger-100 bg-danger-50 px-4 py-3 text-sm text-danger-600">
                    {t(formError)}
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-600">{t('Proveedor')}</label>
                    <select
                      value={formState.supplierId}
                      onChange={(event) =>
                        setFormState((prev) => ({ ...prev, supplierId: event.target.value }))
                      }
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                      required
                    >
                      {suppliers.map((supplier) => (
                        <option key={supplier.id} value={supplier.id}>
                          {t(supplier.name)} {t('· Riesgo')} {t(supplier.riskLevel)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-600">{t('Tipo de OC')}</label>
                    <select
                      value={formState.type}
                      onChange={(event) =>
                        setFormState((prev) => ({ ...prev, type: event.target.value }))
                      }
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                    >
                      <option value="internacional">{t('Internacional · Materiales')}</option>
                      <option value="nacional">{t('Nacional · Servicios')}</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-600">{t('Solicitud de pedido')}</label>
                    <select
                      value={formState.linkedRequisitionId || ''}
                      onChange={(event) => {
                        const nextId = event.target.value;
                        if (!nextId) {
                          setFormState((prev) => ({ ...prev, linkedRequisitionId: '' }));
                          setFormError('');
                          return;
                        }
                        const requisition = pendingRequisitions.find((req) => req.id === nextId);
                        if (requisition) {
                          setFormError('');
                          applyRequisitionToForm(requisition);
                        }
                      }}
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                      disabled={isEditing}
                    >
                      <option value="">{t('Sin solicitud asociada')}</option>
                      {pendingRequisitions.map((req) => (
                        <option key={req.id} value={req.id}>
                          {t(getRequisitionLabel(req))}
                        </option>
                      ))}
                    </select>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <input
                        type="text"
                        value={manualPrInput}
                        onChange={(event) => setManualPrInput(event.target.value.toUpperCase())}
                        className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm"
                        placeholder={t('Ingresa número PR (ej: PR-1024)')}
                        disabled={isEditing}
                      />
                      <button
                        type="button"
                        className="px-3 py-2 rounded-xl border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                        disabled={isEditing}
                        onClick={() => {
                          if (!manualPrInput.trim()) {
                            setFormError('Ingresa un número de PR válido.');
                            return;
                          }
                          const requisition = pendingRequisitions.find(
                            (req) => req.id.toUpperCase() === manualPrInput.trim().toUpperCase()
                          );
                          if (!requisition) {
                            setFormError('La PR no existe o no está disponible para convertir.');
                            return;
                          }
                          applyRequisitionToForm(requisition);
                        }}
                      >{t('Aplicar PR')}</button>
                    </div>
                    {pendingRequisitions.length === 0 && (
                      <p className="text-xs text-slate-400">{t('No hay solicitudes pendientes para convertir.')}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-600">{t('Parque')}</label>
                    <select
                      value={formState.parkId}
                      onChange={(event) => {
                        const nextParkId = event.target.value;
                        const park = findParkById(nextParkId) || parks.find((p) => p.id === nextParkId);
                        const firstArea = park?.areas?.[0];
                        setFormState((prev) => ({
                          ...prev,
                          parkId: nextParkId,
                          areaId: firstArea?.id || '',
                          costCenterId: firstArea?.costCenterId || ''
                        }));
                      }}
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                      required
                    >
                      {parks.map((park) => (
                        <option key={park.id} value={park.id}>
                          {t(park.name)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-600">{t('Área')}</label>
                    <select
                      value={formState.areaId}
                      onChange={(event) => {
                        const nextAreaId = event.target.value;
                        const area = findAreaById(nextAreaId);
                        setFormState((prev) => ({
                          ...prev,
                          areaId: nextAreaId,
                          costCenterId: area?.costCenterId || prev.costCenterId
                        }));
                      }}
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                      required
                    >
                      {parkAreas.map((area) => (
                        <option key={area.id} value={area.id}>
                          {t(area.name)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-600">{t('Centro de costo')}</label>
                    <input
                      type="text"
                      value={currentCostCenter ? `${currentCostCenter.code} · ${currentCostCenter.name}` : 'Sin asignar'}
                      readOnly
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl bg-slate-50 text-slate-500"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-600">{t('Categoría de gasto')}</label>
                    <select
                      value={formState.categoryId}
                      onChange={(event) =>
                        setFormState((prev) => ({ ...prev, categoryId: event.target.value }))
                      }
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                      required
                    >
                      {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {t(category.name)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-600">{t('Ubicación entrega')}</label>
                    <select
                      value={formState.deliveryLocationId}
                      onChange={(event) =>
                        setFormState((prev) => ({ ...prev, deliveryLocationId: event.target.value }))
                      }
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                      required
                    >
                      {locations.map((location) => (
                        <option key={location.id} value={location.id}>
                          {t(location.name)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-600">{t('Fecha estimada')}</label>
                    <input
                      type="date"
                      value={formState.deliveryDate}
                      onChange={(event) =>
                        setFormState((prev) => ({ ...prev, deliveryDate: event.target.value }))
                      }
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-slate-600 uppercase tracking-wider">{t('Ítems de la orden')}</h4>
                    <button
                      type="button"
                      onClick={addItemRow}
                      className="text-sm font-semibold text-emerald-600 hover:text-emerald-700"
                    >{t('+ Añadir ítem')}</button>
                  </div>

                  <div className="space-y-4">
                    {formState.items.map((item, index) => (
                      <div
                        key={`item-${index}`}
                        className="grid grid-cols-1 md:grid-cols-[1.5fr_2.5fr_repeat(3,minmax(0,1fr))] gap-4 bg-slate-50 border border-slate-100 rounded-2xl p-4"
                      >
                        <div className="space-y-2">
                          <label className="text-xs font-semibold text-slate-500">{t('SKU / Código')}</label>
                          <input
                            type="text"
                            value={item.sku}
                            onChange={(event) => handleItemChange(index, 'sku', event.target.value)}
                            className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:ring-2 focus:ring-emerald-500/30"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-xs font-semibold text-slate-500">{t('Descripción')}</label>
                          <input
                            type="text"
                            value={item.description}
                            onChange={(event) => handleItemChange(index, 'description', event.target.value)}
                            className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:ring-2 focus:ring-emerald-500/30"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-xs font-semibold text-slate-500">{t('Cantidad')}</label>
                          <input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(event) => handleItemChange(index, 'quantity', event.target.value)}
                            className="num w-full px-3 py-2 rounded-xl border border-slate-200 focus:ring-2 focus:ring-emerald-500/30"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-xs font-semibold text-slate-500">{t('Unidad')}</label>
                          <input
                            type="text"
                            value={item.unit}
                            onChange={(event) => handleItemChange(index, 'unit', event.target.value)}
                            className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:ring-2 focus:ring-emerald-500/30"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-xs font-semibold text-slate-500">{t('Valor Unitario')}</label>
                          <input
                            type="number"
                            min="0"
                            value={item.unitPrice}
                            onChange={(event) => handleItemChange(index, 'unitPrice', event.target.value)}
                            className="num w-full px-3 py-2 rounded-xl border border-slate-200 focus:ring-2 focus:ring-emerald-500/30"
                          />
                          {formState.items.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeItemRow(index)}
                              className="text-xs text-danger-500 hover:text-danger-600 font-semibold"
                            >{t('Eliminar')}</button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-600">{t('Notas / Comentarios')}</label>
                    <textarea
                      value={formState.notes}
                      onChange={(event) =>
                        setFormState((prev) => ({ ...prev, notes: event.target.value }))
                      }
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                      rows={4}
                    />
                  </div>
                  <div className="bg-slate-50 border border-slate-100 rounded-2xl p-6">
                    <div className="flex items-center gap-3">
                      <ClipboardList className="w-6 h-6 text-neutral-400" />
                      <div>
                        <p className="text-sm font-semibold text-slate-700">{t('Resumen de la orden')}</p>
                        <p className="text-xs text-slate-500">{t('Valor neto calculado en base a cantidades y precios unitarios.')}</p>
                      </div>
                    </div>
                    <div className="mt-4 flex items-center justify-between">
                      <p className="text-sm text-slate-500">{t('Valor total')}</p>
                      <p className="text-xl font-bold text-slate-900">{formatCurrency(orderTotal)}</p>
                    </div>
                    <p className="text-xs text-slate-400 mt-2">{t(
                      'Las órdenes internacionales actualizan inventario al ser recepcionadas. Las nacionales sólo registran servicio.'
                    )}</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="block text-sm font-semibold text-slate-600">{t('Adjuntar archivos (PDF, cotizaciones, catálogos)')}</label>
                  <input
                    type="file"
                    accept=".pdf,image/*,.xlsx,.xls"
                    multiple
                    onChange={handleAttachmentUpload}
                    className="w-full px-4 py-3 border border-dashed border-neutral-300 rounded-xl text-sm text-slate-500 bg-neutral-50 hover:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
                  />
                  {uploadError && <p className="text-xs text-danger-500">{t(uploadError)}</p>}
                  {formState.attachments.length > 0 && (
                    <div className="space-y-2">
                      {formState.attachments.map((attachment) => (
                        <div
                          key={attachment.id}
                          className="flex items-center justify-between px-3 py-2 bg-slate-50 border border-slate-100 rounded-xl text-xs text-slate-600"
                        >
                          <div>
                            <p className="font-semibold text-slate-800">{t(attachment.name)}</p>
                            <p className="text-[11px] text-slate-400">
                              {formatFileSize(attachment.size)}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeAttachment(attachment.id)}
                            className="text-danger-500 font-semibold hover:text-danger-600"
                          >{t('Quitar')}</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {isEditing && (
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-600">{t('Nota para Gerencia de Compras')}</label>
                    <textarea
                      value={editNote}
                      onChange={(event) => setEditNote(event.target.value)}
                      required
                      rows={3}
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500"
                      placeholder={t('Describe brevemente el ajuste contable realizado.')}
                    />
                    <p className="text-xs text-slate-400">{t(
                      'Esta nota se registrará en la bitácora y será visible para la Gerencia de Compras durante la re-aprobación.'
                    )}</p>
                  </div>
                )}

                <div className="flex items-center justify-end gap-3">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      setShowCreateModal(false);
                      resetForm();
                      setModalMode('create');
                      setEditingOrderId(null);
                      setEditNote('');
                    }}
                  >{t('Cancelar')}</Button>
                  <Button type="submit" variant="primary">
                    {isEditing ? t('Guardar cambios') : t('Crear orden')}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

      {selectedOrder && (
          <div className="fixed inset-0 bg-slate-900/50 flex justify-end z-40">
            <div className="w-full max-w-xl h-full bg-white border-l border-slate-100 shadow-2xl p-8 overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <p className="text-sm text-slate-500">{t('Detalle de Orden · #')}{t(selectedOrder.id)}</p>
                  <h3 className="text-xl font-bold text-slate-900">{t(selectedOrder.supplierName)}</h3>
                </div>
                <div className="flex items-center gap-2">
                  {canEditSelectedOrder && (
                    <Button variant="secondary" size="sm" icon={PenSquare} onClick={() => openEditModal(selectedOrder)}>{t('Editar OC')}</Button>
                  )}
                  <button
                    onClick={() => setSelectedOrder(null)}
                    className="text-sm text-slate-500 hover:text-slate-900 transition"
                  >{t('cerrar')}</button>
                </div>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                    <p className="text-xs text-slate-500 uppercase">{t('Estado')}</p>
                    <div className="mt-2">
                      <Badge tone={getStatusTone(selectedOrder.status)}>{t(getStatusText(selectedOrder.status))}</Badge>
                    </div>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                    <p className="text-xs text-slate-500 uppercase">{t('Entrega')}</p>
                    <p className="mt-2 text-sm font-semibold text-slate-800">
                      {t(
                        locations.find((location) => location.id === selectedOrder.deliveryLocationId)?.name
                      ) || t('No especificado')}
                    </p>
                    <p className="text-xs text-slate-400">
                      {selectedOrder.deliveryDate ? formatDate(selectedOrder.deliveryDate) : t('Por asignar')}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                    <p className="text-xs text-slate-500 uppercase">{t('Parque / Área')}</p>
                    <p className="mt-2 text-sm font-semibold text-slate-800">
                      {t(selectedPark?.name) || t('Sin asignar')}
                    </p>
                    <p className="text-xs text-slate-400">{t(selectedArea?.name) || '-'}</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                    <p className="text-xs text-slate-500 uppercase">{t('Centro de costo')}</p>
                    <p className="mt-2 text-sm font-semibold text-slate-800">
                      {selectedCostCenter ? `${selectedCostCenter.code}` : '-'}
                    </p>
                    <p className="text-xs text-slate-400">{t(selectedCostCenter?.name) || ''}</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                    <p className="text-xs text-slate-500 uppercase">{t('Categoría')}</p>
                    <p className="mt-2 text-sm font-semibold text-slate-800">
                      {t(selectedCategory?.name) || t(selectedOrder.categoryName) || t('Sin categoría')}
                    </p>
                    <p className="text-xs text-slate-400">{selectedOrder.type === 'internacional' ? t('Material') : t('Servicio')}</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 md:col-span-2">
                    <p className="text-xs text-slate-500 uppercase">{t('Paquete / WBS')}</p>
                    <p className="mt-2 text-sm font-semibold text-slate-800">
                      {selectedPackage
                        ? `${selectedPackage.code ? `${selectedPackage.code} · ` : ''}${selectedPackage.name}`
                        : t('Sin paquete')}
                    </p>
                  </div>
                </div>

                <div className="bg-white border border-slate-100 rounded-xl">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                    <p className="text-sm font-semibold text-slate-700">{t('Ítems')}</p>
                    <span className="text-xs text-slate-400">
                      {t(selectedOrder.items.length)} {t('registros')}</span>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {selectedOrder.items.map((item) => (
                      <div key={item.id || item.sku} className="px-4 py-3 text-sm text-slate-600">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-semibold text-slate-800">{t(item.description)}</p>
                            <p className="text-xs text-slate-400">{t('SKU')} {t(item.sku) || t('N/A')}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-slate-800">
                              {t(item.quantity)} {t(item.unit)}
                            </p>
                            <p className="text-xs text-slate-400">{formatCurrency(item.unitPrice)}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white border border-slate-100 rounded-xl">
                  <div className="px-4 py-3 border-b border-slate-100">
                    <p className="text-sm font-semibold text-slate-700">{t('Seguimiento ERP')}</p>
                    <p className="text-xs text-slate-400">{t('Recepción · Facturación · Pago')}</p>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {lifecycleEntries.map((entry) => {
                      const completed = entry.node.status === 'completed';
                      const responsible = entry.node.userId
                        ? findUserById(entry.node.userId)?.name || entry.node.userId
                        : 'Pendiente';
                      return (
                        <div key={entry.key} className="px-4 py-4 space-y-3">
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <p className="text-sm font-semibold text-slate-800">{t(entry.title)}</p>
                              <p className="text-xs text-slate-500">{t(entry.description)}</p>
                              <p className="text-xs text-slate-400 mt-2">
                                {completed
                                  ? t(
                                  'Actualizado por {0} · {1}',
                                  responsible,
                                  formatTimestamp(entry.node.timestamp)
                                )
                                  : t('Pendiente de actualización')}
                              </p>
                              {entry.node.referenceId && (
                                <p className="text-xs text-neutral-500 mt-1">{t('Referencia:')} {t(entry.node.referenceId)}
                                </p>
                              )}
                              {entry.node.note && (
                                <p className="text-xs text-slate-500 mt-1 italic">
                                  “{t(entry.node.note)}”
                                </p>
                              )}
                            </div>
                            <Badge tone={completed ? 'success' : 'neutral'}>
                              {completed ? t('Completado') : t('Pendiente')}
                            </Badge>
                          </div>
                          {entry.canAction && entry.renderForm && (
                            <div className="space-y-2">{t(entry.renderForm())}</div>
                          )}
                          {entry.canAction && entry.onAction && (
                            <div className="flex justify-end">
                              <Button variant="primary" size="sm" onClick={entry.onAction}>
                                {t(entry.actionLabel)}
                              </Button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {selectedOrder.attachments && selectedOrder.attachments.length > 0 && (
                  <div className="bg-white border border-slate-100 rounded-xl">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                      <p className="text-sm font-semibold text-slate-700">{t('Documentos adjuntos')}</p>
                      <span className="text-xs text-slate-400">
                        {t(selectedOrder.attachments.length)} {t('archivo(s)')}</span>
                    </div>
                    <div className="divide-y divide-slate-100">
                      {selectedOrder.attachments.map((attachment) => (
                        <div
                          key={attachment.id}
                          className="px-4 py-3 flex items-center justify-between text-sm text-slate-600"
                        >
                          <div>
                            <p className="font-semibold text-slate-800">{t(attachment.name)}</p>
                            <p className="text-xs text-slate-400">
                              {formatFileSize(attachment.size)} ·{' '}
                              {attachment.uploadedAt ? formatDate(attachment.uploadedAt) : t('Sin fecha')}
                            </p>
                          </div>
                          {attachment.dataUrl ? (
                            <a
                              href={attachment.dataUrl}
                              download={attachment.name}
                              className="text-xs font-semibold text-brand-600 hover:text-brand-700"
                            >{t('Descargar')}</a>
                          ) : (
                            <span className="text-xs text-slate-400">{t('Adjunto demo')}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedOrder.notes && (
                  <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                    <p className="text-xs text-slate-500 uppercase mb-2">{t('Notas')}</p>
                    <p className="text-sm text-slate-600">{t(selectedOrder.notes)}</p>
                  </div>
                )}

                {budgetImpact && budgetImpact.totalBudget > 0 && (
                  <div className="bg-white border border-slate-100 rounded-xl p-4 space-y-3">
                    <p className="text-xs text-slate-500 uppercase">{t('Impacto presupuestario')}</p>
                    <div className="flex items-center justify-between text-sm text-slate-600">
                      <div>
                        <p className="font-semibold text-slate-800">{t(budgetImpact.parkName)}</p>
                        <p className="text-xs text-slate-400">{t('Presupuesto total:')} {formatCurrency(budgetImpact.totalBudget)}</p>
                      </div>
                      <div className="text-right text-xs text-slate-500">
                        <p>{t('Comprometido:')} {formatCurrency(budgetImpact.totalCommitted)}</p>
                        <p>{t('Facturado:')} {formatCurrency(budgetImpact.totalInvoiced)}</p>
                      </div>
                    </div>
                    {budgetAllocation && (
                      <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
                        <p className="text-xs text-slate-500 uppercase">{t(budgetAllocation.name)}</p>
                        <p className="text-xs text-slate-400">{t('Presupuesto categoría:')} {formatCurrency(budgetAllocation.amount)}</p>
                        <p className="text-xs text-slate-400">{t('Comprometido categoría:')} {formatCurrency(budgetAllocation.committed)}</p>
                        <p className="text-xs text-slate-400">{t('Disponible categoría:')} {formatCurrency(budgetAllocation.available)}</p>
                      </div>
                    )}
                  </div>
                )}

                {budgetOverrunActive && (
                  <div className="bg-warning-50 border border-warning-100 rounded-xl p-4 space-y-3">
                    <p className="text-xs text-warning-700 uppercase font-semibold">{t('Control de incremento presupuestario')}</p>
                    <p className="text-sm text-warning-700">{t(
                      'Usted no puede avanzar sin esto: la orden excede el presupuesto disponible de la categoría.'
                    )}</p>
                    <div className="text-xs text-warning-700 space-y-1">
                      <p>{t('Disponible categoría:')} {formatCurrency(budgetAvailableAmount)}</p>
                      <p>{t('Exceso detectado:')} {formatCurrency(budgetOverrunAmount)}</p>
                    </div>
                    <label className="flex items-center gap-2 text-sm text-warning-700">
                      <input
                        type="checkbox"
                        className="h-4 w-4"
                        checked={pmBudgetAccepted}
                        disabled={!canConfirmBudgetIncrement}
                        onChange={(event) => {
                          const accepted = event.target.checked;
                          if (!selectedOrder) return;
                          const note = accepted
                            ? window.prompt('Comentario opcional de aceptación de incremento de budget:') || ''
                            : '';
                          setPurchaseOrderBudgetIncrementAcceptance(selectedOrder.id, accepted, note);
                        }}
                      />{t('Gerente de Proyecto acepta incremento de budget')}</label>
                    {!canConfirmBudgetIncrement && (
                      <p className="text-xs text-warning-700">{t(
                        'Solo el Gerente de Proyecto asignado (o super_user) puede confirmar esta aceptación.'
                      )}</p>
                    )}
                    {pmBudgetAccepted && (
                      <p className="text-xs text-success-700">{t('Incremento aceptado por')} {t(findUserById(budgetControl?.acceptedByUserId)?.name) || 'usuario'}·{' '}
                        {budgetControl?.acceptedAt ? formatTimestamp(budgetControl.acceptedAt) : t('sin fecha')}
                      </p>
                    )}
                  </div>
                )}

                <div className="bg-white border border-slate-100 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-semibold text-slate-700">{t('Flujo de aprobaciones')}</p>
                    <span className="text-xs text-slate-400">{t('Multietapa')}</span>
                  </div>
                  <div className="space-y-3">
                    {[{
                      key: 'projectManager',
                      label: t('Project Manager'),
                      stage: 'project'
                    }, {
                      key: 'purchasingManager',
                      label: t('Gerencia de Compras'),
                      stage: 'purchasing'
                    }].map((stage) => {
                      const approval = selectedOrder.approvals?.[stage.key];
                      const approver = approval?.userId ? findUserById(approval.userId) : null;
                      const isPending = (approval?.status || 'pendiente') === 'pendiente';
                      const isChecked = approval?.status === 'aprobada';
                      const blockedByBudget =
                        stage.stage === 'purchasing' && budgetOverrunActive && !pmBudgetAccepted;
                      const canToggle =
                        isPending && approver?.id && currentUser?.id === approver.id && !blockedByBudget;
                      return (
                        <div key={stage.key} className="flex items-center justify-between text-sm">
                          <div>
                            <p className="font-semibold text-slate-800">{t(stage.label)}</p>
                            <p className="text-xs text-slate-400">{t(approver?.name) || t('No asignado')}</p>
                            {blockedByBudget && (
                              <p className="text-xs text-amber-600 mt-1">{t(
                                'Bloqueado: falta aceptación del incremento de budget por el Gerente de Proyecto.'
                              )}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-3">
                            <label className="flex items-center gap-2 text-xs text-slate-500">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                disabled={!canToggle}
                                onChange={() => {
                                  if (!canToggle) return;
                                  advancePurchaseOrderApproval(
                                    selectedOrder.id,
                                    stage.stage,
                                    true,
                                    observation
                                  );
                                  setObservation('');
                                }}
                                className="h-4 w-4"
                              />{t('Aprobado')}</label>
                            <div className="text-right">
                            <Badge tone={getStatusTone(approval?.status || 'pendiente')}>
                              {t(getStatusText(approval?.status || 'pendiente'))}
                            </Badge>
                            <p className="text-[11px] text-slate-400 mt-1">
                              {approval?.date ? formatDate(approval.date) : t('Sin fecha')}
                            </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="bg-white border border-slate-100 rounded-xl p-4">
                  <p className="text-xs text-slate-500 uppercase mb-2">{t('Acciones de aprobación')}</p>
                  <textarea
                    value={observation}
                    onChange={(event) => setObservation(event.target.value)}
                    placeholder={t('Comentario / Observación')}
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-slate-900/10"
                    rows={3}
                  />
                  {selectedStage && (
                    <p className="text-xs text-slate-400 mt-2">{t('Próxima aprobación:')} {t(getStatusText(selectedOrder.status))} {t('· Responsable:')}{' '}
                      {selectedStage === 'project' &&
                        t(findUserById(selectedOrder.approvals?.projectManager?.userId)?.name)}
                      {selectedStage === 'purchasing' &&
                        t(findUserById(selectedOrder.approvals?.purchasingManager?.userId)?.name)}
                    </p>
                  )}
                  <div className="mt-3 flex flex-wrap gap-2">
                    {isCurrentApprover && selectedStage && (
                      <>
                        <Button variant="primary" icon={CheckCircle2} onClick={() => handleApproval(selectedOrder, true)}>{t('Aprobar')}</Button>
                        <Button variant="danger" icon={XCircle} onClick={() => handleApproval(selectedOrder, false)}>{t('Rechazar')}</Button>
                      </>
                    )}
                    {selectedOrder.status === 'aprobada' &&
                      ['purchasing_manager', 'comprador', 'super_user'].includes(currentUser.role) && (
                        <Button variant="secondary" onClick={handleSendToSupplier}>{t('Enviar a proveedor')}</Button>
                      )}
                    {selectedOrder.status === 'aprobada' &&
                      ['purchasing_manager', 'comprador', 'super_user'].includes(currentUser.role) && (
                        <Button variant="secondary" icon={PackageOpen} onClick={() => handleReception(selectedOrder)}>{t('Marcar Recepción')}</Button>
                      )}
                  </div>
                </div>

                <div className="bg-slate-50 border border-slate-100 rounded-xl p-4">
                  <p className="text-xs text-slate-500 uppercase mb-2">{t('Historial de la orden')}</p>
                  <div className="space-y-3">
                    {(selectedOrder.history || []).map((entry) => (
                      <div key={entry.id} className="flex items-center justify-between text-sm text-slate-600">
                        <div>
                          <p className="font-semibold text-slate-800">{t(entry.action)}</p>
                          <p className="text-xs text-slate-400">{t(entry.note)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-slate-400">{t(entry.timestamp)}</p>
                          <p className="text-xs text-slate-400">
                            {t(findUserById(entry.userId)?.name) || t('Sistema')}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Button variant="secondary" icon={FileDown} onClick={handleExportOrder}>{t('Exportar Orden (PDF)')}</Button>
                </div>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default PurchaseOrders;
